'use client';import { Theme } from '@parkingbase/ecommerce-api-test';


import { useThemeStore } from '@/stores/theme.store';

import { useEffect } from 'react';
import { generateThemeCSSVariablesWithMode } from '@/lib/theme-utils';

function applyThemeToDocument(theme: Theme, isDarkMode: boolean) {
  if (typeof window === 'undefined' || !theme) return;

  const root = document.documentElement;
  const styles = generateThemeCSSVariablesWithMode(theme, isDarkMode);

  if (styles) {
    Object.entries(styles).forEach(([cssVar, value]) => {
      root.style.setProperty(cssVar, value);
    });
  }

  root.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
}

export function ThemeProvider({
  children,
  defaultTheme,
}: {
  defaultTheme: Theme | undefined;
  children: React.ReactNode;
}) {
  const { setTheme, theme, isDarkMode, setThemeMode } = useThemeStore();

  /* Hydrate theme mode from localStorage on mount */
  useEffect(() => {
    const stored = localStorage.getItem('dark-mode');
    if (stored === 'dark' || stored === 'system') {
      setThemeMode(stored);
    } else if (stored === 'true') {
      // Migrate old boolean format
      setThemeMode('dark');
    }
  }, [setThemeMode]);

  /* Listen for system color scheme changes when mode is 'system' */
  useEffect(() => {
    const { themeMode } = useThemeStore.getState();
    if (themeMode !== 'system') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const currentMode = useThemeStore.getState().themeMode;
      if (currentMode === 'system') {
        useThemeStore.setState({ isDarkMode: e.matches });
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [isDarkMode]);

  /* Set the default theme from server side */
  useEffect(() => {
    if (defaultTheme) {
      setTheme(defaultTheme);
    }
  }, [defaultTheme, setTheme]);

  /* Apply the theme to the document */
  useEffect(() => {
    if (theme) {
      applyThemeToDocument(theme, isDarkMode);
    }
  }, [theme, isDarkMode]);

  return <>{children}</>;
}
